import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Save, Calendar, Euro, Clock, Phone, Type, Image as ImageIcon, MapPin,
  Trash2, Plus, Check, X, RefreshCw, Sun,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { id: "bookings", label: "Prenotazioni", icon: Calendar },
  { id: "lessons", label: "Lezioni & Prezzi", icon: Euro },
  { id: "schedule", label: "Orari", icon: Clock },
  { id: "texts", label: "Testi", icon: Type },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "events", label: "Eventi", icon: Sun },
  { id: "location", label: "Location", icon: MapPin },
  { id: "contact", label: "Contatti", icon: Phone },
];

// Admin editing language (shared by Field/Area for bilingual {it,en} values)
let ADMIN_LANG = "it";
const isBi = (v) => v && typeof v === "object" && !Array.isArray(v);

const Field = ({ label, value, onChange, type = "text", testid, biLang }) => {
  const lang = biLang || ADMIN_LANG;
  const bi = isBi(value);
  const display = bi ? (value[lang] ?? "") : (value ?? "");
  const handle = (raw) => {
    if (type === "number") return onChange(Number(raw));
    if (bi) return onChange({ ...value, [lang]: raw });
    onChange(raw);
  };
  return (
    <label className="block">
      <span className="text-xs font-mono text-ocean/50 uppercase tracking-wider">{label}</span>
      <input
        type={type}
        value={display}
        onChange={(e) => handle(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-white border border-ocean/15 rounded-lg px-3 py-2 text-ocean focus:outline-none focus:border-coral"
      />
    </label>
  );
};

const Area = ({ label, value, onChange, testid, biLang }) => {
  const lang = biLang || ADMIN_LANG;
  const bi = isBi(value);
  const display = bi ? (value[lang] ?? "") : (value ?? "");
  const handle = (raw) => (bi ? onChange({ ...value, [lang]: raw }) : onChange(raw));
  return (
    <label className="block">
      <span className="text-xs font-mono text-ocean/50 uppercase tracking-wider">{label}</span>
      <textarea
        value={display}
        onChange={(e) => handle(e.target.value)}
        data-testid={testid}
        rows={3}
        className="mt-1 w-full bg-white border border-ocean/15 rounded-lg px-3 py-2 text-ocean focus:outline-none focus:border-coral resize-none"
      />
    </label>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const { content, setContent, refresh } = useContent();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [draft, setDraft] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aLang, setALang] = useState("it");
  ADMIN_LANG = aLang;

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  const loadBookings = () => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  };
  useEffect(() => { if (user) loadBookings(); }, [user]);

  if (!user || !draft) {
    return <div className="min-h-screen bg-ocean-900 flex items-center justify-center text-sand-100 font-mono">Caricamento...</div>;
  }

  const set = (path, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", draft);
      setContent(data);
      toast.success("Modifiche salvate!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      loadBookings();
      toast.success("Stato aggiornato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteBooking = async (id) => {
    try { await api.delete(`/bookings/${id}`); loadBookings(); toast.success("Eliminata"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-sand-200" data-testid="admin-page">
      <header className="bg-ocean-900 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="light" className="h-9 w-auto" />
            <span className="hidden sm:block font-mono text-xs tracking-[0.2em] text-gold">GESTIONE</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full border border-white/20 text-xs font-mono overflow-hidden mr-1">
              {["it", "en"].map((l) => (
                <button key={l} onClick={() => setALang(l)} data-testid={`admin-lang-${l}`} className={`px-2.5 py-1 uppercase transition-colors ${aLang === l ? "bg-coral text-white" : "text-sand-100/70"}`}>{l}</button>
              ))}
            </div>
            {tab !== "bookings" && (
              <button onClick={save} disabled={saving} data-testid="admin-save-button" className="inline-flex items-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold text-sm px-4 py-2 rounded-full disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? "Salvo..." : "Salva"}
              </button>
            )}
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sand-100/70 hover:text-gold text-sm px-3 py-2">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-6">
        {/* tabs */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-6 pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full font-mono text-xs tracking-wider transition-colors ${
                  tab === t.id ? "bg-ocean text-sand-100" : "bg-white text-ocean/60 hover:text-ocean border border-ocean/10"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* BOOKINGS */}
        {tab === "bookings" && (
          <div data-testid="admin-bookings">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-ocean">Prenotazioni ({bookings.length})</h2>
              <button onClick={loadBookings} className="inline-flex items-center gap-2 text-ocean/60 text-sm"><RefreshCw className="w-4 h-4" /> Aggiorna</button>
            </div>
            {bookings.length === 0 ? (
              <p className="text-ocean/50">Nessuna prenotazione ancora.</p>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-white rounded-xl border border-ocean/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-gold">{b.reference}</span>
                        <span className={`font-mono text-[0.6rem] uppercase px-2 py-0.5 rounded-full ${b.status === "confirmed" ? "bg-green-100 text-green-700" : b.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-ocean/10 text-ocean/60"}`}>{b.status}</span>
                      </div>
                      <div className="font-heading font-bold text-ocean">{b.first_name} {b.last_name} · {b.lesson_name}</div>
                      <div className="text-sm text-ocean/60 font-mono">{b.date} {b.time} · {b.participants}p · {b.phone} · {b.email}</div>
                      {b.notes && <div className="text-sm text-ocean/50 italic mt-1">"{b.notes}"</div>}
                    </div>
                    <div className="flex gap-2">
                      {b.status !== "confirmed" && <button onClick={() => setBookingStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`} className="p-2 rounded-lg bg-green-100 text-green-700" title="Conferma"><Check className="w-4 h-4" /></button>}
                      {b.status !== "cancelled" && <button onClick={() => setBookingStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`} className="p-2 rounded-lg bg-amber-100 text-amber-700" title="Annulla"><X className="w-4 h-4" /></button>}
                      <button onClick={() => deleteBooking(b.id)} data-testid={`delete-${b.id}`} className="p-2 rounded-lg bg-red-100 text-red-600" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* LESSONS & PRICES */}
        {tab === "lessons" && (
          <div className="space-y-6" data-testid="admin-lessons">
            <h2 className="font-heading text-xl font-bold text-ocean">Lezioni</h2>
            {draft.lessons?.map((l, i) => (
              <div key={l.id} className="bg-white rounded-xl border border-ocean/10 p-4 grid sm:grid-cols-2 gap-3">
                <Field label="Nome" value={l.name} onChange={(v) => set(["lessons", i, "name"], v)} testid={`lesson-name-${i}`} />
                <Field label="Livello" value={l.level} onChange={(v) => set(["lessons", i, "level"], v)} />
                <Field label="Prezzo (€)" type="number" value={l.price} onChange={(v) => set(["lessons", i, "price"], v)} testid={`lesson-price-${i}`} />
                <Field label="Immagine URL" value={l.image} onChange={(v) => set(["lessons", i, "image"], v)} />
                <div className="sm:col-span-2"><Area label="Descrizione" value={l.description} onChange={(v) => set(["lessons", i, "description"], v)} /></div>
                <div className="sm:col-span-2"><Field label="Incluso (separato da virgola)" value={(l.includes || []).map((x) => (isBi(x) ? (x[aLang] ?? "") : x)).join(", ")} onChange={(v) => set(["lessons", i, "includes"], v.split(",").map((s, idx) => { const cur = l.includes?.[idx]; return isBi(cur) ? { ...cur, [aLang]: s.trim() } : { it: s.trim(), en: s.trim() }; }).filter((o) => (o[aLang] || "").length))} /></div>
              </div>
            ))}

            <h2 className="font-heading text-xl font-bold text-ocean pt-4">Listino Prezzi</h2>
            <Area label="Nota listino" value={draft.prices?.note} onChange={(v) => set(["prices", "note"], v)} />
            {draft.prices?.items?.map((it, i) => (
              <div key={i} className="bg-white rounded-xl border border-ocean/10 p-4 grid sm:grid-cols-4 gap-3 items-end">
                <Field label="Voce" value={it.name} onChange={(v) => set(["prices", "items", i, "name"], v)} />
                <Field label="Prezzo (€)" type="number" value={it.price} onChange={(v) => set(["prices", "items", i, "price"], v)} testid={`price-item-${i}`} />
                <Field label="Unità" value={it.unit} onChange={(v) => set(["prices", "items", i, "unit"], v)} />
                <Field label="Descrizione" value={it.description} onChange={(v) => set(["prices", "items", i, "description"], v)} />
              </div>
            ))}
          </div>
        )}

        {/* SCHEDULE */}
        {tab === "schedule" && (
          <div className="space-y-4 max-w-xl" data-testid="admin-schedule">
            <h2 className="font-heading text-xl font-bold text-ocean">Orari & Disponibilità</h2>
            <p className="text-sm text-ocean/60">Ogni lezione occupa automaticamente 1h 20m. Gli slot qui sotto sono gli orari di inizio proposti ai clienti.</p>
            <Field
              label="Orari slot (HH:MM separati da virgola)"
              value={(draft.schedule?.slot_times || []).join(", ")}
              onChange={(v) => set(["schedule", "slot_times"], v.split(",").map((s) => s.trim()).filter(Boolean))}
              testid="schedule-slots"
            />
            <Area label="Nota orari" value={draft.schedule?.note} onChange={(v) => set(["schedule", "note"], v)} />
          </div>
        )}

        {/* TEXTS */}
        {tab === "texts" && (
          <div className="space-y-6 max-w-2xl" data-testid="admin-texts">
            <div className="bg-white rounded-xl border border-ocean/10 p-4 space-y-3">
              <h3 className="font-heading font-bold text-ocean">Hero</h3>
              <Field label="Eyebrow" value={draft.hero?.eyebrow} onChange={(v) => set(["hero", "eyebrow"], v)} />
              <Area label="Slogan" value={draft.hero?.slogan} onChange={(v) => set(["hero", "slogan"], v)} />
              <Field label="Immagine sfondo URL" value={draft.hero?.bg_image} onChange={(v) => set(["hero", "bg_image"], v)} />
              <Field label="CTA primaria" value={draft.hero?.cta_primary} onChange={(v) => set(["hero", "cta_primary"], v)} />
              <Field label="CTA secondaria" value={draft.hero?.cta_secondary} onChange={(v) => set(["hero", "cta_secondary"], v)} />
            </div>
            <div className="bg-white rounded-xl border border-ocean/10 p-4 space-y-3">
              <h3 className="font-heading font-bold text-ocean">Salty Folks (About)</h3>
              <Field label="Titolo" value={draft.about?.heading} onChange={(v) => set(["about", "heading"], v)} />
              <Field label="Immagine URL" value={draft.about?.image} onChange={(v) => set(["about", "image"], v)} />
              {draft.about?.paragraphs?.map((p, i) => (
                <Area key={i} label={`Paragrafo ${i + 1}`} value={p} onChange={(v) => set(["about", "paragraphs", i], v)} />
              ))}
            </div>
            <div className="bg-white rounded-xl border border-ocean/10 p-4 space-y-3">
              <h3 className="font-heading font-bold text-ocean">SEO</h3>
              <Field label="Title" value={draft.seo?.title} onChange={(v) => set(["seo", "title"], v)} />
              <Area label="Meta description" value={draft.seo?.description} onChange={(v) => set(["seo", "description"], v)} />
            </div>
          </div>
        )}

        {/* GALLERY */}
        {tab === "gallery" && (
          <div className="space-y-4" data-testid="admin-gallery">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ocean">Gallery ({draft.gallery?.length || 0})</h2>
              <button onClick={() => set(["gallery"], [...(draft.gallery || []), { url: "", alt: "", category: "Action" }])} data-testid="gallery-add" className="inline-flex items-center gap-2 bg-ocean text-sand-100 text-sm font-heading font-bold px-4 py-2 rounded-full"><Plus className="w-4 h-4" /> Aggiungi</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {draft.gallery?.map((g, i) => (
                <div key={i} className="bg-white rounded-xl border border-ocean/10 p-4 space-y-3">
                  <div className="flex gap-3">
                    {g.url && <img src={g.url} alt={g.alt} className="w-20 h-20 rounded-lg object-cover shrink-0" />}
                    <div className="flex-1 space-y-2">
                      <Field label="Immagine URL" value={g.url} onChange={(v) => set(["gallery", i, "url"], v)} testid={`gallery-url-${i}`} />
                      <Field label="Categoria" value={g.category} onChange={(v) => set(["gallery", i, "category"], v)} />
                    </div>
                  </div>
                  <Field label="Alt text (SEO)" value={g.alt} onChange={(v) => set(["gallery", i, "alt"], v)} />
                  <button onClick={() => set(["gallery"], draft.gallery.filter((_, k) => k !== i))} data-testid={`gallery-remove-${i}`} className="inline-flex items-center gap-1 text-red-500 text-sm"><Trash2 className="w-4 h-4" /> Rimuovi</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EVENTS */}
        {tab === "events" && (
          <div className="space-y-4" data-testid="admin-events">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ocean">Eventi ({draft.events?.length || 0})</h2>
              <button onClick={() => set(["events"], [...(draft.events || []), { id: "event-" + Date.now(), name: { it: "", en: "" }, image: "", date: { it: "", en: "" }, time: "", place: { it: "", en: "" }, description: { it: "", en: "" } }])} data-testid="event-add" className="inline-flex items-center gap-2 bg-ocean text-sand-100 text-sm font-heading font-bold px-4 py-2 rounded-full"><Plus className="w-4 h-4" /> Aggiungi</button>
            </div>
            {draft.events?.map((ev, i) => (
              <div key={i} className="bg-white rounded-xl border border-ocean/10 p-4 grid sm:grid-cols-2 gap-3">
                {ev.image && <img src={ev.image} alt="" className="sm:col-span-2 h-32 w-full object-cover rounded-lg" />}
                <Field label="Nome" value={ev.name} onChange={(v) => set(["events", i, "name"], v)} testid={`event-name-${i}`} />
                <Field label="Immagine URL" value={ev.image} onChange={(v) => set(["events", i, "image"], v)} />
                <Field label="Data" value={ev.date} onChange={(v) => set(["events", i, "date"], v)} />
                <Field label="Orario" value={ev.time} onChange={(v) => set(["events", i, "time"], v)} />
                <Field label="Luogo" value={ev.place} onChange={(v) => set(["events", i, "place"], v)} />
                <div className="sm:col-span-2"><Area label="Descrizione" value={ev.description} onChange={(v) => set(["events", i, "description"], v)} /></div>
                <button onClick={() => set(["events"], draft.events.filter((_, k) => k !== i))} data-testid={`event-remove-${i}`} className="inline-flex items-center gap-1 text-red-500 text-sm"><Trash2 className="w-4 h-4" /> Rimuovi</button>
              </div>
            ))}
          </div>
        )}

        {/* LOCATION */}
        {tab === "location" && (
          <div className="space-y-3 max-w-2xl" data-testid="admin-location">
            <h2 className="font-heading text-xl font-bold text-ocean">Location</h2>
            <Field label="Nome" value={draft.location?.name} onChange={(v) => set(["location", "name"], v)} />
            <Field label="Indirizzo" value={draft.location?.address} onChange={(v) => set(["location", "address"], v)} />
            <Field label="Google Maps embed URL" value={draft.location?.maps_embed} onChange={(v) => set(["location", "maps_embed"], v)} />
            <Field label="Get Directions URL" value={draft.location?.directions_url} onChange={(v) => set(["location", "directions_url"], v)} />
          </div>
        )}

        {/* CONTACT */}
        {tab === "contact" && (
          <div className="space-y-3 max-w-xl" data-testid="admin-contact">
            <h2 className="font-heading text-xl font-bold text-ocean">Contatti & Social</h2>
            <Field label="WhatsApp (con prefisso, es +39...)" value={draft.contact?.whatsapp} onChange={(v) => set(["contact", "whatsapp"], v)} testid="contact-whatsapp-input" />
            <Field label="Telefono" value={draft.contact?.phone} onChange={(v) => set(["contact", "phone"], v)} />
            <Field label="Email" value={draft.contact?.email} onChange={(v) => set(["contact", "email"], v)} />
            <Field label="Instagram handle" value={draft.contact?.instagram} onChange={(v) => set(["contact", "instagram"], v)} />
            <Field label="Instagram URL" value={draft.contact?.instagram_url} onChange={(v) => set(["contact", "instagram_url"], v)} />
            <Field label="Facebook URL" value={draft.contact?.facebook_url} onChange={(v) => set(["contact", "facebook_url"], v)} />
          </div>
        )}
      </div>
    </div>
  );
}
